"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Sidebar from '../components/layout/Sidebar';
import MobileNavigation from '../components/layout/MobileNavigation';
import MobileHeader from '../components/layout/MobileHeader';
import Image from 'next/image';
import { useCheckout } from '../context/CheckoutContext';
import { FaArrowLeft, FaShoppingCart, FaPlus, FaMinus, FaTimes, FaLocationArrow, FaCreditCard, FaRegCreditCard, FaMoneyBillWave, FaRegCheckCircle } from 'react-icons/fa';
import { MdDeliveryDining, MdLocalOffer } from 'react-icons/md';

// Currency formatter for Pakistani Rupees
const formatCurrency = (amount) => {
  // Ensure the amount is a number
  const numAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  
  // Format with thousand separators
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount).replace('PKR', 'Rs.');
};

export default function CheckoutPage() {
  const router = useRouter();
  const {
    cartItems,
    selectedRestaurant,
    checkoutStep,
    deliveryDetails,
    paymentDetails,
    order,
    calculateOrderSummary,
    updateCartItemQuantity,
    removeFromCart,
    setDeliveryDetails,
    setPaymentDetails,
    nextStep,
    prevStep
  } = useCheckout();
  
  // State for mobile sidebar
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Check if cart is empty and redirect if it is
  useEffect(() => {
    if (cartItems.length === 0 && !order) {
      router.push('/');
    }
  }, [cartItems, order, router]);
  
  // Summary calculations
  const { subtotal, deliveryFee, platformFee, total } = calculateOrderSummary();
  
  // Handle input change for delivery details
  const handleDeliveryInputChange = (e) => {
    const { name, value } = e.target;
    setDeliveryDetails({
      ...deliveryDetails,
      [name]: value
    });
  };
  
  // Handle input change for payment details
  const handlePaymentInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentDetails({
      ...paymentDetails,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Generate text for continue button
  const getContinueButtonText = () => {
    if (checkoutStep === 'cart') return 'Proceed to Delivery';
    if (checkoutStep === 'delivery') return 'Proceed to Payment';
    if (checkoutStep === 'payment') return 'Place Order';
    return 'Back to Home';
  };
  
  // Handle continue button click
  const handleContinue = () => {
    if (checkoutStep === 'confirmation') {
      router.push('/');
      return;
    }
    
    // Validate inputs before proceeding
    if (checkoutStep === 'delivery') {
      if (!deliveryDetails.name || !deliveryDetails.phone || !deliveryDetails.address || !deliveryDetails.city) {
        alert('Please fill all required fields in delivery details');
        return;
      }
    }
    
    if (checkoutStep === 'payment') {
      if (paymentDetails.method === 'card') {
        if (!paymentDetails.cardNumber || !paymentDetails.cardName || !paymentDetails.expiryDate || !paymentDetails.cvv) {
          alert('Please fill all card details');
          return;
        }
      } else if (['easypaisa', 'jazzcash'].includes(paymentDetails.method)) {
        if (!paymentDetails.mobileNumber) {
          alert('Please enter a mobile number');
          return;
        }
      }
    }
    
    nextStep();
  };
  
  // Handle back button click
  const handleBack = () => {
    if (checkoutStep === 'cart') {
      router.push('/');
      return;
    }
    
    prevStep();
  };
  
  // Render cart step
  const renderCartStep = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Review Your Order</h2>
      
      {selectedRestaurant && (
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative w-16 h-16 rounded-full overflow-hidden bg-primary text-white flex items-center justify-center">
              {selectedRestaurant.image && selectedRestaurant.image.length === 2 && /\p{Emoji}/u.test(selectedRestaurant.image) ? (
                <span className="text-2xl">{selectedRestaurant.image}</span>
              ) : (
                <Image
                  src={typeof selectedRestaurant.image === 'string' && selectedRestaurant.image.startsWith('/') ? 
                    selectedRestaurant.image : '/images/default-restaurant.png'}
                  alt={selectedRestaurant.name}
                  fill
                  className="object-cover"
                />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold">{selectedRestaurant.name}</h3>
              <p className="text-sm text-gray-500">{selectedRestaurant.address}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="border-t border-gray-200 pt-4">
        <h3 className="font-semibold text-lg mb-3">Items in Your Cart</h3>
        
        {cartItems.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-primary text-white flex items-center justify-center">
                {item.image && item.image.length === 2 && /\p{Emoji}/u.test(item.image) ? (
                  <span className="text-2xl">{item.image}</span>
                ) : (
                  <Image
                    src={typeof item.image === 'string' && item.image.startsWith('/') ? 
                      item.image : '/images/default-food.png'}
                    alt={item.name}
                    fill
                    className="object-cover"
                  />
                )}
              </div>
              <div>
                <h4 className="text-md font-medium">{item.name}</h4>
                <p className="text-sm text-gray-500">
                  {formatCurrency(item.calculatedPrice)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-200 rounded-md">
                <button
                  className="px-2 py-1 text-primary"
                  onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                >
                  <FaMinus size={12} />
                </button>
                <span className="px-3 py-1">{item.quantity}</span>
                <button
                  className="px-2 py-1 text-primary"
                  onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                >
                  <FaPlus size={12} />
                </button>
              </div>
              <button
                className="text-red-500"
                onClick={() => removeFromCart(item.id)}
              >
                <FaTimes size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery Fee</span>
            <span>{formatCurrency(deliveryFee)}</span>
          </div>
          <div className="flex justify-between">
            <span>Platform Fee</span>
            <span>{formatCurrency(platformFee)}</span>
          </div>
          <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render delivery step
  const renderDeliveryStep = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Delivery Details</h2>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={deliveryDetails.name}
              onChange={handleDeliveryInputChange}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={deliveryDetails.phone}
              onChange={handleDeliveryInputChange}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder="Enter your phone number"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Delivery Address *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={deliveryDetails.address}
            onChange={handleDeliveryInputChange}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="Enter your street address"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">City *</label>
            <select
              id="city"
              name="city"
              value={deliveryDetails.city}
              onChange={handleDeliveryInputChange}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            >
              <option value="Karachi">Karachi</option>
              <option value="Lahore">Lahore</option>
              <option value="Islamabad">Islamabad</option>
              <option value="Rawalpindi">Rawalpindi</option>
              <option value="Faisalabad">Faisalabad</option>
            </select>
          </div>
        </div>
        
        <div>
          <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-1">Delivery Instructions (Optional)</label>
          <textarea
            id="instructions"
            name="instructions"
            value={deliveryDetails.instructions}
            onChange={handleDeliveryInputChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
            placeholder="Any special instructions for delivery"
            rows={3}
          />
        </div>
        
        <div className="mt-4">
          <h3 className="font-semibold text-lg mb-3">Delivery Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`border ${deliveryDetails.deliveryMethod === 'standard' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
              onClick={() => handleDeliveryInputChange({ target: { name: 'deliveryMethod', value: 'standard' } })}
            >
              <div className="flex items-center mb-2">
                <input
                  type="radio"
                  id="standard"
                  name="deliveryMethod"
                  value="standard"
                  checked={deliveryDetails.deliveryMethod === 'standard'}
                  onChange={handleDeliveryInputChange}
                  className="mr-2"
                />
                <label htmlFor="standard" className="text-md font-medium">Standard Delivery</label>
              </div>
              <p className="text-sm text-gray-500">Estimated delivery: 40-50 minutes</p>
              <p className="text-sm mt-1">{formatCurrency(120)}</p>
            </div>
            
            <div
              className={`border ${deliveryDetails.deliveryMethod === 'express' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
              onClick={() => handleDeliveryInputChange({ target: { name: 'deliveryMethod', value: 'express' } })}
            >
              <div className="flex items-center mb-2">
                <input
                  type="radio"
                  id="express"
                  name="deliveryMethod"
                  value="express"
                  checked={deliveryDetails.deliveryMethod === 'express'}
                  onChange={handleDeliveryInputChange}
                  className="mr-2"
                />
                <label htmlFor="express" className="text-md font-medium">Express Delivery</label>
              </div>
              <p className="text-sm text-gray-500">Estimated delivery: 25-30 minutes</p>
              <p className="text-sm mt-1">{formatCurrency(220)}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery Fee</span>
            <span>{formatCurrency(deliveryFee)}</span>
          </div>
          <div className="flex justify-between">
            <span>Platform Fee</span>
            <span>{formatCurrency(platformFee)}</span>
          </div>
          <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render payment step
  const renderPaymentStep = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Method</h2>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className={`border ${paymentDetails.method === 'cod' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
            onClick={() => handlePaymentInputChange({ target: { name: 'method', value: 'cod' } })}
          >
            <div className="flex items-center">
              <input
                type="radio"
                id="cod"
                name="method"
                value="cod"
                checked={paymentDetails.method === 'cod'}
                onChange={handlePaymentInputChange}
                className="mr-2"
              />
              <label htmlFor="cod" className="flex items-center">
                <FaMoneyBillWave className="text-green-600 mr-2" size={18} />
                <span className="text-md font-medium">Cash On Delivery</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6 mt-1">Pay with cash upon delivery</p>
          </div>
          
          <div
            className={`border ${paymentDetails.method === 'card' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
            onClick={() => handlePaymentInputChange({ target: { name: 'method', value: 'card' } })}
          >
            <div className="flex items-center">
              <input
                type="radio"
                id="card"
                name="method"
                value="card"
                checked={paymentDetails.method === 'card'}
                onChange={handlePaymentInputChange}
                className="mr-2"
              />
              <label htmlFor="card" className="flex items-center">
                <FaRegCreditCard className="text-blue-600 mr-2" size={18} />
                <span className="text-md font-medium">Credit/Debit Card</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6 mt-1">Pay with Visa, Mastercard, etc.</p>
          </div>
          
          <div
            className={`border ${paymentDetails.method === 'easypaisa' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
            onClick={() => handlePaymentInputChange({ target: { name: 'method', value: 'easypaisa' } })}
          >
            <div className="flex items-center">
              <input
                type="radio"
                id="easypaisa"
                name="method"
                value="easypaisa"
                checked={paymentDetails.method === 'easypaisa'}
                onChange={handlePaymentInputChange}
                className="mr-2"
              />
              <label htmlFor="easypaisa" className="flex items-center">
                <span className="text-green-600 font-bold mr-2">EP</span>
                <span className="text-md font-medium">Easypaisa</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6 mt-1">Pay with your Easypaisa account</p>
          </div>
          
          <div
            className={`border ${paymentDetails.method === 'jazzcash' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
            onClick={() => handlePaymentInputChange({ target: { name: 'method', value: 'jazzcash' } })}
          >
            <div className="flex items-center">
              <input
                type="radio"
                id="jazzcash"
                name="method"
                value="jazzcash"
                checked={paymentDetails.method === 'jazzcash'}
                onChange={handlePaymentInputChange}
                className="mr-2"
              />
              <label htmlFor="jazzcash" className="flex items-center">
                <span className="text-red-600 font-bold mr-2">JC</span>
                <span className="text-md font-medium">JazzCash</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6 mt-1">Pay with your JazzCash account</p>
          </div>
          
          {/* Google Pay Option */}
          <div
            className={`border ${paymentDetails.method === 'googlepay' ? 'border-primary' : 'border-gray-200'} rounded-md p-4 cursor-pointer`}
            onClick={() => handlePaymentInputChange({ target: { name: 'method', value: 'googlepay' } })}
          >
            <div className="flex items-center">
              <input
                type="radio"
                id="googlepay"
                name="method"
                value="googlepay"
                checked={paymentDetails.method === 'googlepay'}
                onChange={handlePaymentInputChange}
                className="mr-2"
              />
              <label htmlFor="googlepay" className="flex items-center">
                <span className="bg-white text-blue-600 font-bold mr-2 flex items-center">
                  <span className="text-blue-500">G</span>
                  <span className="text-red-500">P</span>
                </span>
                <span className="text-md font-medium">Google Pay</span>
              </label>
            </div>
            <p className="text-sm text-gray-500 ml-6 mt-1">Pay using Google Pay</p>
          </div>
        </div>
        
        {/* Card details */}
        {paymentDetails.method === 'card' && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-lg mb-3">Card Details</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">Card Number *</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={paymentDetails.cardNumber}
                  onChange={handlePaymentInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder="1234 5678 9012 3456"
                  maxLength={16}
                />
              </div>
              <div>
                <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name *</label>
                <input
                  type="text"
                  id="cardName"
                  name="cardName"
                  value={paymentDetails.cardName}
                  onChange={handlePaymentInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder="Enter cardholder name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
                  <input
                    type="text"
                    id="expiryDate"
                    name="expiryDate"
                    value={paymentDetails.expiryDate}
                    onChange={handlePaymentInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                    placeholder="MM/YY"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">CVV *</label>
                  <input
                    type="text"
                    id="cvv"
                    name="cvv"
                    value={paymentDetails.cvv}
                    onChange={handlePaymentInputChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                    placeholder="123"
                    maxLength={3}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="savePaymentInfo"
                  name="savePaymentInfo"
                  checked={paymentDetails.savePaymentInfo}
                  onChange={handlePaymentInputChange}
                  className="mr-2"
                />
                <label htmlFor="savePaymentInfo" className="text-sm text-gray-700">Save this card for future payments</label>
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Payment details */}
        {['easypaisa', 'jazzcash', 'googlepay'].includes(paymentDetails.method) && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-lg mb-3">
              {paymentDetails.method === 'easypaisa' ? 'Easypaisa' : 
               paymentDetails.method === 'jazzcash' ? 'JazzCash' : 'Google Pay'} Details
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  value={paymentDetails.mobileNumber}
                  onChange={handlePaymentInputChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder="03XX XXXXXXX"
                />
              </div>
              {paymentDetails.method === 'googlepay' && (
                <p className="text-sm text-gray-500">
                  You will receive a payment request on your Google Pay app.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="mt-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Delivery Fee</span>
            <span>{formatCurrency(deliveryFee)}</span>
          </div>
          <div className="flex justify-between">
            <span>Platform Fee</span>
            <span>{formatCurrency(platformFee)}</span>
          </div>
          <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render confirmation step
  const renderConfirmationStep = () => (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <FaRegCheckCircle className="text-green-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Order Confirmed!</h2>
        <p className="text-gray-600 mt-2">Your order has been placed successfully</p>
      </div>
      
      {order && (
        <div className="border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Order #{order.id}</h3>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
              {order.status}
            </span>
          </div>
          
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-12 h-12 rounded-full overflow-hidden bg-primary text-white flex items-center justify-center">
                {order.restaurant.image && order.restaurant.image.length === 2 && /\p{Emoji}/u.test(order.restaurant.image) ? (
                  <span className="text-xl">{order.restaurant.image}</span>
                ) : (
                  <Image
                    src={typeof order.restaurant.image === 'string' && order.restaurant.image.startsWith('/') ? 
                      order.restaurant.image : '/images/default-restaurant.png'}
                    alt={order.restaurant.name}
                    fill
                    className="object-cover"
                  />
                )}
              </div>
              <div>
                <h4 className="font-medium">{order.restaurant.name}</h4>
                <p className="text-sm text-gray-500">{order.restaurant.address}</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between">
                  <span>{item.quantity}x {item.name}</span>
                  <span>{formatCurrency(item.calculatedPrice * item.quantity)}</span>
                </div>
              ))}
            </div>
            
            <div className="border-t border-gray-100 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Delivery Fee</span>
                <span>{formatCurrency(order.deliveryFee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Platform Fee</span>
                <span>{formatCurrency(order.platformFee)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>{formatCurrency(order.total)}</span>
              </div>
            </div>
          </div>
          
          <div className="mt-6 space-y-4">
            <div>
              <h4 className="font-medium mb-2">Delivery Details</h4>
              <p className="text-sm">{order.delivery.name}</p>
              <p className="text-sm">{order.delivery.phone}</p>
              <p className="text-sm">{order.delivery.address}, {order.delivery.city}</p>
              {order.delivery.instructions && (
                <p className="text-sm mt-1">
                  <span className="font-medium">Instructions:</span> {order.delivery.instructions}
                </p>
              )}
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Payment Method</h4>
              <p className="text-sm">
                {order.payment.method === 'cod' && 'Cash On Delivery'}
                {order.payment.method === 'card' && 'Credit/Debit Card'}
                {order.payment.method === 'easypaisa' && 'Easypaisa'}
                {order.payment.method === 'jazzcash' && 'JazzCash'}
                {order.payment.method === 'googlepay' && 'Google Pay'}
              </p>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Estimated Delivery Time</h4>
              <p className="text-sm">
                {order.delivery.deliveryMethod === 'express' ? '25-30 minutes' : '40-50 minutes'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="text-center mt-6">
        <p className="text-gray-600 mb-4">
          We&apos;ve sent a confirmation to your phone. You can track your order in the &quot;My Orders&quot; section.
        </p>
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <button
            className="bg-primary text-white py-2 px-6 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
            onClick={() => router.push('/orders')}
          >
            View My Orders
          </button>
          <button
            className="bg-white border border-gray-300 text-gray-700 py-2 px-6 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
            onClick={() => router.push('/')}
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
  
  // Determine which step to render
  const renderCurrentStep = () => {
    if (checkoutStep === 'cart') return renderCartStep();
    if (checkoutStep === 'delivery') return renderDeliveryStep();
    if (checkoutStep === 'payment') return renderPaymentStep();
    if (checkoutStep === 'confirmation') return renderConfirmationStep();
    return null;
  };
  
  // Render checkout steps indicator
  const renderStepsIndicator = () => {
    const steps = [
      { id: 'cart', label: 'Cart', icon: <FaShoppingCart /> },
      { id: 'delivery', label: 'Delivery', icon: <FaLocationArrow /> },
      { id: 'payment', label: 'Payment', icon: <FaCreditCard /> },
      { id: 'confirmation', label: 'Confirmation', icon: <FaRegCheckCircle /> }
    ];
    
    return (
      <div className="hidden md:flex items-center justify-center mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                checkoutStep === step.id
                  ? 'bg-primary text-white'
                  : ['cart', 'delivery', 'payment'].indexOf(checkoutStep) >= ['cart', 'delivery', 'payment'].indexOf(step.id)
                    ? 'bg-primary-light text-primary'
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step.icon}
            </div>
            <span
              className={`ml-2 ${
                checkoutStep === step.id ? 'text-primary font-medium' : 'text-gray-500'
              }`}
            >
              {step.label}
            </span>
            {index < steps.length - 1 && (
              <div className={`w-16 h-1 mx-3 ${
                ['cart', 'delivery', 'payment'].indexOf(checkoutStep) > index
                  ? 'bg-primary'
                  : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          className="mr-4 p-2 rounded-full hover:bg-gray-100 transition"
          onClick={handleBack}
        >
          <FaArrowLeft className="text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold">Checkout</h1>
      </div>
      
      {renderStepsIndicator()}
      
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-3/4">
          {renderCurrentStep()}
          
          <div className="mt-6 flex justify-between">
            <button
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
              onClick={handleBack}
            >
              {checkoutStep === 'cart' ? 'Continue Shopping' : 'Back'}
            </button>
            
            <button
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              onClick={handleContinue}
            >
              {getContinueButtonText()}
            </button>
          </div>
        </div>
        
        <div className="md:w-1/4">
          {checkoutStep !== 'confirmation' && (
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-4">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              
              {selectedRestaurant && (
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 rounded-full overflow-hidden bg-primary text-white flex items-center justify-center">
                      {selectedRestaurant.image && selectedRestaurant.image.length === 2 && /\p{Emoji}/u.test(selectedRestaurant.image) ? (
                        <span className="text-xl">{selectedRestaurant.image}</span>
                      ) : (
                        <Image
                          src={typeof selectedRestaurant.image === 'string' && selectedRestaurant.image.startsWith('/') ? 
                            selectedRestaurant.image : '/images/default-restaurant.png'}
                          alt={selectedRestaurant.name}
                          fill
                          className="object-cover"
                        />
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium">{selectedRestaurant.name}</h4>
                      <p className="text-xs text-gray-500">{cartItems.length} item(s)</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span>{formatCurrency(deliveryFee)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Platform Fee</span>
                  <span>{formatCurrency(platformFee)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
              
              {/* Promo code section */}
              <div className="mt-6">
                <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Promo Code
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="promoCode"
                    className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-2 focus:ring-primary focus:outline-none"
                    placeholder="Enter code"
                  />
                  <button className="bg-primary text-white px-4 py-2 rounded-r-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50">
                    Apply
                  </button>
                </div>
              </div>
              
              {/* Delivery time estimate */}
              <div className="mt-6 flex items-center">
                <MdDeliveryDining className="text-primary mr-2" size={24} />
                <div>
                  <h4 className="text-sm font-medium">Estimated Delivery Time</h4>
                  <p className="text-xs text-gray-500">
                    {deliveryDetails.deliveryMethod === 'express' ? '25-30 minutes' : '40-50 minutes'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 